// Talen: Nederlands (standaard, zonder voorvoegsel), Frans (/fr/…) en Engels (/en/…).
// De Nederlandse tekst in de code is zelf de sleutel: t('Inloggen') geeft in het Frans 'Se connecter'.
// Ontbreekt een vertaling, dan blijft de Nederlandse tekst staan. Gedeeld tussen browser en server.
//
//   t('Wat snoei je in {maand}?', { maand })      variabelen tussen accolades
//   t(n === 1 ? '{n} soort' : '{n} soorten', { n })  enkelvoud en meervoud apart
//
// De vertalingen staan per onderdeel in public/js/i18n/ui/*.js (registerStrings), de soortgegevens in
// public/js/i18n/data/<taal>/*.js.

export const LANGS = ['nl', 'fr', 'en'];
export const LANG_NAMES = { nl: 'Nederlands', fr: 'Français', en: 'English' };
export const LOCALES = { nl: 'nl-BE', fr: 'fr-BE', en: 'en-GB' };
export const OG_LOCALES = { nl: 'nl_BE', fr: 'fr_BE', en: 'en_GB' };

const dicts = { fr: Object.create(null), en: Object.create(null) };
// Teksten die de beheerder aanpaste (Backoffice › Teksten, zie src/content.js), ook in het Nederlands. Ze gaan voor
// de vertaling in de code. In de browser komen ze mee in de pagina, op de server uit de database.
let custom = { nl: Object.create(null), fr: Object.create(null), en: Object.create(null) };
let current = 'nl';

/** Vertalingen toevoegen: { fr: { 'Nederlands': 'Français' }, en: { … } }. */
export function registerStrings(table) {
  for (const lang of ['fr', 'en']) Object.assign(dicts[lang], table[lang] || {});
}

/** Alle vertalingen van één taal (voor de build, die per taal een eigen bestand maakt: scripts/build.js). */
export const stringsFor = (lang) => ({ ...(dicts[lang] || {}) });

const fill = (s, vars) => (vars ? String(s).replace(/\{(\w+)\}/g, (m, k) => (k in vars ? String(vars[k]) : m)) : String(s));

/** De aangepaste teksten instellen: { nl: { 'Nederlandse tekst': 'aangepast' }, fr: { … }, en: { … } }. */
export function setCustomTexts(table) {
  custom = Object.fromEntries(LANGS.map((l) => [l, Object.assign(Object.create(null), table?.[l] || {})]));
}

/** De tekst zoals ze in de code staat (de vertaling, zonder aanpassing van de beheerder), of null. */
export const standardText = (lang, s) => (lang === 'nl' ? s : dicts[lang]?.[s] ?? null);

/** Vertaling in een bepaalde taal (op de server, waar elk verzoek een andere taal kan hebben). */
export function tr(lang, s, vars) {
  if (s == null) return '';
  const l = LANGS.includes(lang) ? lang : 'nl';
  const out = custom[l][s] ?? (l !== 'nl' ? dicts[l][s] : null);
  return fill(out ?? s, vars);
}

/**
 * Een bericht dat pas bij het tonen vertaald wordt: een tekst, of [tekst, velden]. Velden met ~ vooraan worden
 * zelf ook vertaald (in kleine letters), met $ vooraan vertaald zoals ze zijn, en met @ vooraan als datum
 * (YYYY-MM-DD) getoond: ['{naam} vroeg {soort} aan op {datum}.', { naam, '~soort': 'Snoeien', '@datum': '2026-11-20' }].
 */
export function trMessage(lang, msg) {
  if (!Array.isArray(msg)) return tr(lang, msg);
  // Meerdere zinnen na elkaar: [[zin, velden], [zin, velden]].
  if (Array.isArray(msg[0])) return msg.map((m) => trMessage(lang, m)).filter(Boolean).join(' ');
  const [s, vars = {}] = msg;
  const out = {};
  for (const [k, v] of Object.entries(vars)) {
    if (k.startsWith('~')) out[k.slice(1)] = tr(lang, v).toLowerCase();
    else if (k.startsWith('$')) out[k.slice(1)] = tr(lang, v);
    else if (k.startsWith('@')) out[k.slice(1)] = v ? new Intl.DateTimeFormat(locale(lang), { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' }).format(new Date(`${String(v).slice(0, 10)}T12:00:00Z`)) : '';
    else out[k] = v;
  }
  return tr(lang, s, out);
}

/** Voert `fn` (synchroon) uit alsof de pagina in `lang` staat: zo maakt de server teksten in de taal van een ontvanger. */
export function inLang(lang, fn) {
  const prev = current;
  current = LANGS.includes(lang) ? lang : 'nl';
  try {
    return fn();
  } finally {
    current = prev;
  }
}

/** Vertaling in de taal van de pagina (in de browser). */
/**
 * Een tekst in een lijst of tabel die pas later met t(label) vertaald wordt (menu, delen van een fiche, filters). Doet
 * niets, maar zo vinden de controle op vertalingen en Backoffice › Teksten de tekst (src/ui-strings.js).
 */
export const tl = (s) => s;
export const t = (s, vars) => tr(current, s, vars);
export const hasTranslation = (lang, s) => lang === 'nl' || !!dicts[lang]?.[s];

export const getLang = () => current;
export function setLang(lang) {
  current = LANGS.includes(lang) ? lang : 'nl';
}
export const locale = (lang = current) => LOCALES[lang] || LOCALES.nl;

// Paden die nooit een taalvoorvoegsel krijgen.
const NO_PREFIX = /^\/(api|cal|vendor|docs|img|css|js|fonts|uploads)(\/|$)/;

/** De taal van een pad: /fr/soorten → 'fr', /soorten → 'nl'. */
export function langFromPath(path) {
  const m = /^\/(fr|en)(?=\/|$)/.exec(String(path || ''));
  return m ? m[1] : 'nl';
}

/** Het pad zonder taalvoorvoegsel: /fr/soort/zomereik → /soort/zomereik. */
export function stripLang(path) {
  return String(path || '/').replace(/^\/(fr|en)(?=\/|$)/, '') || '/';
}

/** Het pad in een taal: withLang('/soorten', 'fr') → '/fr/soorten'. Querystring en hekje blijven. */
export function withLang(path, lang = current) {
  const p = String(path || '/');
  if (!p.startsWith('/') || p.startsWith('//') || NO_PREFIX.test(p)) return p;
  const bare = stripLang(p.replace(/[?#].*$/, '')) + (p.match(/[?#].*$/)?.[0] || '');
  if (lang === 'nl') return bare;
  return `/${lang}${bare === '/' ? '' : bare.startsWith('/?') || bare.startsWith('/#') ? bare.slice(1) : bare}`;
}

/** Naam van een maand (0 = januari) in de taal van de pagina: maart, mars, March. */
const monthFormats = new Map();
export function monthName(i, lang = current) {
  const loc = locale(lang);
  if (!monthFormats.has(loc)) monthFormats.set(loc, new Intl.DateTimeFormat(loc, { month: 'long' }));
  return monthFormats.get(loc).format(new Date(2026, i, 15));
}
